import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import moment from 'moment'

import {
  createActiveCoachingForAgent,
  fetchActiveCoachingForAgent,
  updateActiveCoachingConfig,
} from '@/reducers/tasks/tasks.actions'
import { Banner } from '@/components/banners/Banner'
import { Card } from '@/components/cards/Card'
import { Toggle } from '@/components/forms/Checkbox'
import { ExternalLink } from '@/components/ExternalLink'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { getActiveCoachingPopupContent } from '@/reducers/tasks/tasks.helpers'

export const ActiveCoaching = ({ agentId, taskId, question, channel }) => {
  const [activeCoachingConfig, setActiveCoachingConfig] = useState(false)
  const dispatch = useDispatch()
  const currentTaskIsActive =
    activeCoachingConfig?.is_active && taskId === activeCoachingConfig?.task_id
  const agentIsActiveOnAnotherTask =
    activeCoachingConfig?.is_active && taskId !== activeCoachingConfig?.task_id

  const handleActiveCoachingChange = async () => {
    // Use a PUT to deactivate the current task
    if (currentTaskIsActive) {
      const response = await updateActiveCoachingConfig(dispatch, activeCoachingConfig.id, {
        ...activeCoachingConfig,
        is_active: false,
      })
      if (response) {
        setActiveCoachingConfig(response)
      }
    } else {
      // Use a POST to create a new active coaching for this task
      // or to activate this task if the agent has some other active task
      const response = await createActiveCoachingForAgent(dispatch, agentId, taskId, question)
      if (response) {
        setActiveCoachingConfig(response)
      }
    }
  }

  const fetchActiveCoaching = async () => {
    const response = await fetchActiveCoachingForAgent(dispatch, agentId)

    setActiveCoachingConfig(response)
  }

  useEffect(() => {
    if (agentId && taskId && question) {
      fetchActiveCoaching()
    }
  }, [agentId, taskId, question])

  return (
    <Card compact>
      <div className="mbs">
        <InteractiveTooltip content={getActiveCoachingPopupContent(channel)}>
          <Toggle
            label="Enable AI Coaching for this agent"
            data-testid="active-coaching-checkbox"
            checked={currentTaskIsActive}
            onChange={handleActiveCoachingChange}
          />
        </InteractiveTooltip>
      </div>
      <p style={{ paddingLeft: '4.5rem' }}>
        AI Coaching provides personalized tips and examples over 14 days to help agents develop one
        skill at a time. Each agent can participate in one coaching program at a time.
      </p>
      {activeCoachingConfig?.is_active && (
        <Banner informative style={{ marginBottom: 0 }}>
          <strong>Note:</strong> This agent is currently enrolled in an AI Coaching program with{' '}
          {moment(activeCoachingConfig.expiration_date).fromNow(true)} remaining.{' '}
          {agentIsActiveOnAnotherTask && 'Enabling a new program will replace their current one.'}
          <br />
          {agentIsActiveOnAnotherTask && (
            <ExternalLink
              to={`/coaching/inbox?id=${activeCoachingConfig.task_id}`}
              target="_blank"
              label="View currently active program."
            />
          )}
        </Banner>
      )}
    </Card>
  )
}
